import axios from 'axios'
import { toSnakeCase, toCamelCase } from './data-format'
import { getToken, setToken, removeAll, removeToken } from '@/utils/auth'
import { Message, MessageBox } from 'element-ui';
import store from '@/store'
import router from '@/router'
import { refreshAccessToken } from '@/api/security'

const http = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 30000
})

// 401刷新鎖、等待佇列
let isRefreshing = false
let waitQueue = []
// 定時刷新計時器標識
let refreshTimer = null
// accessToken有效期，後端30分鐘，提前5分鐘刷新
const ACCESS_EXPIRE_MS = 25 * 60 * 1000
// 避免重複彈出登錄超時確認框
let isShowLogoutConfirm = false

// 啟動定時靜默刷新
function startTokenTimer() {
    stopTokenTimer()
    refreshTimer = setInterval(async () => {
        const userInfo = store.getters.adminUserInfo || {}
        const rt = userInfo.refreshToken
        if (!rt || !getToken()) {
            stopTokenTimer()
            return
        }
        try {
            const res = await refreshAccessToken({ refreshToken: rt }, { unformat: true })
            setToken(res.data.accessToken)
        } catch (err) {
            stopTokenTimer()
            logoutConfirm()
        }
    }, ACCESS_EXPIRE_MS)
}

// 清除計時器
function stopTokenTimer() {
    if (refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
    }
}

/**
 * 登錄超時：彈出確認視窗，確認才跳轉登錄頁，取消則留在當前頁
 */
function logoutConfirm() {
    stopTokenTimer()
    waitQueue.forEach(cb => cb(null))
    waitQueue = []

    if (isShowLogoutConfirm) return
    isShowLogoutConfirm = true

    MessageBox.confirm(
        '您的登錄已超時，是否返回登錄頁面？',
        '登錄超時',
        {
            confirmButtonText: '確定',
            cancelButtonText: '取消',
            type: 'warning',
            closeOnClickModal: false
        }
    ).then(() => {
        // 確認：清理資料並跳轉登錄，帶回跳位址
        removeToken()
        removeAll()
        store.dispatch("setAdminUserInfo", null)
        const redirect = router.currentRoute.fullPath
        router.replace({ path: '/login', query: { redirect } })
    }).catch(() => {
        // 取消：留在當前頁，不做跳轉
        Message.info('已取消返回登錄')
    }).finally(() => {
        isShowLogoutConfirm = false
    })
}

http.interceptors.request.use(
    config => {
        const token = getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        config.headers['accept-language'] = 'zh_tc'
        if (!config.unformat) {
            config.params = toSnakeCase(config.params)
            config.data = toSnakeCase(config.data)
        }
        return config
    },
    error => Promise.reject(error)
)

http.interceptors.response.use(
    response => {
        if (response.config.url.includes('login')) {
            startTokenTimer()
        }
        if (response.headers['content-type'] === 'application/json') {
            response.data = toCamelCase(response.data)
        }
        return response
    },
    async error => {
        if (!error.response) {
            Message.error('網路異常，請檢查網路連線')
            return Promise.reject(error)
        }
        // 只有後端業務碼告訴你需要登出，才執行
        const refreshStatus = error.response?.status
        const refreshUrl = error.config?.url || ''
        // 這裡！！refresh接口401 且 url包含access_token 才登出
        if (refreshStatus === 401 && refreshUrl.includes('access_token')) {
            logoutConfirm(true)
        }
        const { status } = error.response
        const originConfig = error.config

        // accessToken過期401被動刷新兜底
        if (status === 401 && !originConfig._retry) {
            const userInfo = store.getters.adminUserInfo || {}
            const rt = userInfo.refreshToken
            // 沒有refreshToken → 彈超時確認
            if (!rt) {
                logoutConfirm()
                return Promise.reject(error)
            }

            if (isRefreshing) {
                return new Promise(resolve => {
                    waitQueue.push(newToken => {
                        if (newToken) {
                            originConfig.headers.Authorization = `Bearer ${newToken}`
                            resolve(http(originConfig))
                        } else {
                            // refresh失敗，放棄該請求
                            resolve(Promise.reject(error))
                        }
                    })
                })
            }

            isRefreshing = true
            originConfig._retry = true
            try {
                const res = await refreshAccessToken({ refreshToken: rt }, { unformat: true })
                const newAccess = res.data.accessToken
                setToken(newAccess)
                waitQueue.forEach(cb => cb(newAccess))
                waitQueue = []
                originConfig.headers.Authorization = `Bearer ${newAccess}`
                return http(originConfig)
            } catch (refreshErr) {
                // refreshToken已經失效，彈登錄超時確認
                waitQueue.forEach(cb => cb(null))
                waitQueue = []
                logoutConfirm()
                return Promise.reject(refreshErr)
            } finally {
                isRefreshing = false
            }
        }

        if (status === 500) {
            Message.error(error.response.data?.message || '伺服器錯誤')
        } else if (status !== 401) {
            Message.error(error.response.data?.error_message || '請求失敗')
        }
        return Promise.reject(error)
    }
)

export default http
